/**
 * Generators for the curves and line overlays drawn on the hip spine nomogram.
 *
 * Each exported function returns a NomogramLine expressed in the target
 * coordinate system. The work is done in three stages:
 *   1. Build the line in the standing/FPP frame (CSI formula for bounds,
 *      horizontal line for the Widmer combined-version target).
 *   2. Shift from standing to APP via anteinclination offset.
 *   3. Reproject each point into the target coordinate system.
 */

import { degToRad, radToDeg } from '@/util/math';
import { type LpsCoordinateSystem } from '@/domain/anatomy/LPS';
import { computeCSIAnteversion } from './hipSpineNomogramCalculations';
import { MAX_INCLINATION, MIN_INCLINATION } from './anteversionInclination';
import { INCLINATION_RANGE_EXTENSION, type NomogramLine } from './hipSpineNomogramTypes';
import { convertCupAngles } from './hipSpineNomogramGeometry';

/**
 * Compute a CSI bound line in the standing frame.
 *
 * For each cup inclination, solve for the cup anteversion where:
 *   target = csiBound - pelvicFemoralAngle
 *   cupAnteversion = atan(tan(target) * cos(cupInclination))
 */
function computeCSIBoundLine(
    csiBound: number,
    pelvicFemoralAngle: number,
): NomogramLine {
    const inclinations: number[] = [];
    const anteversions: number[] = [];
    for (
        let cupInclination = MIN_INCLINATION - INCLINATION_RANGE_EXTENSION;
        cupInclination <= MAX_INCLINATION + INCLINATION_RANGE_EXTENSION;
        cupInclination++
    ) {
        const cupAnteversionDeg = computeCSIAnteversion(
            csiBound,
            pelvicFemoralAngle,
            cupInclination,
        );
        inclinations.push(cupInclination);
        anteversions.push(cupAnteversionDeg);
    }

    return { inclination: inclinations, anteversion: anteversions };
}

/**
 * Compute a CSI bound line in the target coordinate system.
 *
 * The CSI line is computed in the standing frame, shifted to APP via
 * anteinclination offset, then expressed as a 3D cup normal using the
 * APP basis vectors and measured in the target coordinate system.
 *
 * @param csiBound - CSI bound angle (e.g. 245° upper, 205° lower).
 * @param pelvicFemoralAngle - Pelvic femoral angle at the HJC vertex.
 * @param appAngle - APP tilt angle in degrees (from hip-spine landmarks).
 * @param appCS - APP coordinate system (basis vectors in CT-LPS space).
 * @param targetCS - Target coordinate system (LPS basis vectors).
 */
export function computeCSIBound(
    csiBound: number,
    pelvicFemoralAngle: number,
    appAngle: number,
    appCS: LpsCoordinateSystem,
    targetCS: LpsCoordinateSystem,
): NomogramLine {
    const standingLine = computeCSIBoundLine(csiBound, pelvicFemoralAngle);
    const appLine = shiftLineByAppAngle(standingLine, appAngle);
    return convertCupAngles(appLine, appCS, targetCS);
}

/**
 * Compute the combined version target line in the target coordinate system.
 *
 * The Widmer formula gives a target cup anteversion in the standing frame,
 * which forms a horizontal line. This is shifted to APP via anteinclination
 * offset, then expressed as a 3D cup normal using the APP basis vectors
 * and measured in the target coordinate system.
 *
 * @param targetAnteversion - Target cup anteversion in the standing frame (degrees), already clamped to [20, 28].
 * @param appAngle - APP tilt angle in degrees (from hip-spine landmarks).
 * @param appCS - APP coordinate system (basis vectors in CT-LPS space).
 * @param targetCS - Target coordinate system (LPS basis vectors).
 */
export function computeCombinedVersionTargetLine(
    targetAnteversion: number,
    appAngle: number,
    appCS: LpsCoordinateSystem,
    targetCS: LpsCoordinateSystem,
): NomogramLine {
    const standingLine = computeStandingCombinedVersionTargetLine(targetAnteversion);
    const appLine = shiftLineByAppAngle(standingLine, appAngle);
    return convertCupAngles(appLine, appCS, targetCS);
}

/**
 * Build a horizontal combined version target line in the standing/FPP frame.
 *
 * Every inclination gets the same constant anteversion value.
 */
function computeStandingCombinedVersionTargetLine(targetAnteversion: number): NomogramLine {
    const inclinations: number[] = [];
    const anteversions: number[] = [];
    for (let inc = MIN_INCLINATION - INCLINATION_RANGE_EXTENSION; inc <= MAX_INCLINATION + INCLINATION_RANGE_EXTENSION; inc++) {
        inclinations.push(inc);
        anteversions.push(targetAnteversion);
    }
    return { inclination: inclinations, anteversion: anteversions };
}

/**
 * Shift a standing-frame line to APP by offsetting anteinclination by appAngle.
 *
 * For each point: standing AV → standing AI, shift by appAngle, then APP AI → APP AV.
 *   AI = atan(tan(AV) / cos(Inc))
 *   AI_APP = AI + appAngle
 *   AV_APP = atan(tan(AI_APP) × cos(Inc))
 */
function shiftLineByAppAngle(line: NomogramLine, appAngle: number): NomogramLine {
    const inclinations: number[] = [];
    const anteversions: number[] = [];
    const appAngleRad = degToRad(appAngle);

    for (let i = 0; i < line.inclination.length; i++) {
        const incRad = degToRad(line.inclination[i]);
        const avRad = degToRad(line.anteversion[i]);
        const cosInc = Math.cos(incRad);

        // Standing AV → standing anteinclination
        const standingAI = Math.atan(Math.tan(avRad) / cosInc);
        // Shift to APP anteinclination
        const appAI = standingAI + appAngleRad;
        // APP anteinclination → APP anteversion
        const appAV = Math.atan(Math.tan(appAI) * cosInc);

        inclinations.push(line.inclination[i]);
        anteversions.push(radToDeg(appAV));
    }

    return { inclination: inclinations, anteversion: anteversions };
}
